import { API_BASE_URL } from "src/config/api";
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Card,
  Container,
  Typography,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  Box,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  Accordion, AccordionSummary, AccordionDetails, Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import ConfirmationDialog from '../../../layouts/shared/ConfirmationDialog';
import { UserListHead, UserListToolbar } from '../user';
import Scrollbar from '../../../components/scrollbar';

const StyledTableCell = styled(TableCell)`
  padding: 16px;
  text-align: center;
`;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? theme.palette.grey[100]
        : theme.palette.action.hover,
  },
}));

const TABLE_HEAD = [
  { id: 'domain', label: <b>Endpoint</b>, alignRight: false },
  { id: 'methodType', label: <b>Method</b>, alignRight: false },
  { id: 'checkInterval', label: <b>Check Interval</b>, alignRight: false },
  { id: 'lastStatus', label: <b>Last Status</b>, alignRight: false },
  { id: 'createdAt', label: <b>CreatedAt</b>, alignRight: false },
  { id: 'action', label: <b>Action</b>, alignRight: false },
];



function DomainAddressDataTable() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('domain');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const token = localStorage.getItem('token');

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedDomainData, setSelectedDomainData] = useState(null);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      const response = await axios.get(`${API_BASE_URL}/getDomainByUserId?userId=${userId}`, config);
      setData(response.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleView = (item) => {
    const lastFiveResponses = item.responses.slice(-5).reverse();
    setSelectedDomainData({
      ...item,
      responses: lastFiveResponses
    });
    setIsViewDialogOpen(true);
  };


  const handleDelete = (_id, e) => {
    e.preventDefault();
    setItemIdToDelete(_id);
    setIsConfirmationDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsConfirmationDialogOpen(false);
    setIsLoading(true);
    try {
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      await axios.post(`${API_BASE_URL}/deleteDomain`, { _id: itemIdToDelete }, config);
      toast.success('Domain deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Unable to delete domain');
      console.error('Error deleting domain:', error);
    } finally {
      setIsLoading(false);
      setItemIdToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmationDialogOpen(false);
    setItemIdToDelete(null);
  };

  const handleRequestSort = (event, property) => {
    if (property === 'actions' || property === 'serial') return;
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) => (a, b) => {
    const valA = a[orderBy];
    const valB = b[orderBy];

    // Handle undefined values
    if (valA === undefined || valB === undefined) {
      return 0;
    }

    const aVal = typeof valA === 'string' ? valA.toLowerCase() : valA;
    const bVal = typeof valB === 'string' ? valB.toLowerCase() : bVal;

    if (order === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }

    return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0];
  };

  const filteredData = stableSort(data, getComparator(order, orderBy)).filter((item) =>
    Object.values(item).join(' ').toLowerCase().includes(filterName.toLowerCase())
  );

  const formatInterval = (minutes) => {
    if (!minutes) return '-';

    if (minutes < 60) {
      return `${minutes} Min`;
    }

    if (minutes === 60) {
      return '1 Hour';
    }

    if (minutes < 1440) {
      return `${minutes / 60} Hours`;
    }

    if (minutes === 1440) {
      return '1 Day';
    }

    return `${minutes / 1440} Days`;
  };


  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getTextColorClass = (transactionType) => {
    switch (transactionType) {
      case 'up':
        return 'green-text';
      case 'down':
        return 'red-text';
      default:
        return '';
    }
  };
  return (
    <Card>
      <Container maxWidth="xl">
        <Typography variant="h6" sx={{ marginTop: '15px', fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Watch List
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} searchName="Search Endpoint" />
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchData}
            sx={{ mr: 2, mt: 3, textTransform: 'none' }}
          >
            <RefreshIcon sx={{ mr: 1 }} />
            Reload Status
          </Button>

        </Box>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 800, overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1rem' }}>
              {isLoading ? (
                <CircularProgress />
              ) : (
                <>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={data.length}
                      onRequestSort={handleRequestSort}
                    />
                    {data.length === 0 ? (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={7}>
                            <Typography variant="body2" color="text.secondary" sx={{ justifyContent: 'center', display: 'flex' }}>
                              No Data Found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : (
                      <TableBody>
                        {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                          const { _id, domain, lastStatus, createdAt, isActive, checkIntervalMinutes } =
                            row;

                          return (
                            <StyledTableRow hover key={_id} tabIndex={-1}>
                              <TableCell padding="normal">{page * rowsPerPage + index + 1}</TableCell>
                              <StyledTableCell>{domain}</StyledTableCell>
                              <StyledTableCell>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontWeight: 'bold',
                                    backgroundColor: row.methodType === 'POST' ? '#e3f2fd' : '#e8f5e9',
                                    color: row.methodType === 'POST' ? '#0d47a1' : '#1b5e20'
                                  }}
                                >
                                  {row.methodType || 'GET'}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontWeight: 'bold',
                                    backgroundColor: '#f3f4f6',
                                    color: '#0d47a1'
                                  }}
                                >
                                  {formatInterval(checkIntervalMinutes)}
                                </Typography>
                              </StyledTableCell>

                              <StyledTableCell >
                                <span className={`balance-cell ${getTextColorClass(lastStatus)}`}>
                                  {capitalizeFirst(lastStatus)}
                                </span>

                                <style>
                                  {`
                                    .balance-cell {
                                      display: inline-block;
                                      padding: 4px 10px;
                                      font-weight: bold;
                                      border-radius: 15%;
                                    }

                                    .green-text {
                                      background-color: #bcf3beff;
                                      color: #1b5e20;
                                    }

                                    .red-text {
                                      background-color: #ecc9c9ff;
                                      color: #b71c1c;
                                    }
                                  `}
                                </style>
                              </StyledTableCell>
                              <StyledTableCell>{formatDate(createdAt)}</StyledTableCell>
                              <StyledTableCell>
                                <Tooltip title="View" arrow>
                                  <IconButton
                                    color="info"
                                    disabled={!isActive}
                                    onClick={() => handleView(row)}
                                    sx={{ mr: 1 }}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Delete" arrow>
                                  <IconButton
                                    color="error"
                                    onClick={(e) => handleDelete(_id, e)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </StyledTableCell>
                            </StyledTableRow>
                          );
                        })}

                        {filteredData.length === 0 && (
                          <StyledTableRow>
                            <StyledTableCell colSpan={7} align="center">
                              <Typography variant="subtitle1" textAlign='center' sx={{ py: 3 }}>
                                {filterName
                                  ? `No results found for "${filterName}"`
                                  : 'No data available.'}
                              </Typography>
                            </StyledTableCell>
                          </StyledTableRow>
                        )}
                      </TableBody>
                    )}
                  </Table>
                </>
              )}
            </div>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Container>
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this?"
      />
      <Dialog open={isViewDialogOpen} onClose={() => setIsViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Last 5 Status Checks
          <IconButton
            aria-label="close"
            onClick={() => setIsViewDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>

          {/* ================= BASIC INFO ================= */}
          <Typography variant="subtitle2">
            <strong>Method:</strong> {selectedDomainData?.methodType || 'GET'}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            <strong>URL:</strong> {selectedDomainData?.domain}
          </Typography>

          {/* ================= POST API DETAILS ================= */}
          {selectedDomainData?.methodType === 'POST' && (
            <>
              {/* CURL */}
              {selectedDomainData?.curl && (
                <Accordion sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>🧪 Curl Command</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {selectedDomainData.curl}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* REQUEST DETAILS */}
              <Accordion sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>📤 Request Details</Typography>
                </AccordionSummary>
                <AccordionDetails>

                  {/* Headers */}
                  {selectedDomainData?.requestConfig?.headers?.length > 0 && (
                    <>
                      <Typography variant="subtitle2">Headers</Typography>
                      {selectedDomainData.requestConfig.headers.map((h, i) => (
                        <Typography key={i} variant="body2">
                          <strong>{h.key}:</strong> {h.value}
                        </Typography>
                      ))}
                    </>
                  )}

                  {/* Params */}
                  {selectedDomainData?.requestConfig?.params?.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Params</Typography>
                      {selectedDomainData.requestConfig.params.map((p, i) => (
                        <Typography key={i} variant="body2">
                          <strong>{p.key}:</strong> {p.value}
                        </Typography>
                      ))}
                    </>
                  )}

                  {/* Body */}
                  {selectedDomainData?.requestConfig?.body && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Body</Typography>
                      <pre style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedDomainData.requestConfig.body}
                      </pre>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {/* ================= LAST 5 RESPONSES ================= */}
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Last 5 Checks
          </Typography>

          {(() => {
            const validResponses = selectedDomainData?.responses?.filter((resp) => resp && typeof resp === "object") || [];

            return validResponses.length > 0 ? (
              <List>
                {validResponses.map((resp, index) => {
                  const hasStatus = resp?.status != null;
                  const isError = hasStatus && resp.status >= 400;

                  return (
                    <ListItem
                      key={index}
                      sx={{ flexDirection: "column", alignItems: "flex-start", mb: 1 }}
                    >
                      {/* Status */}
                      <Typography
                        variant="subtitle2"
                        color={!hasStatus ? "text.secondary": isError? "error": "success.main"}
                      >
                        {!hasStatus
                          ? "⚠️ No Status Available"
                          : isError
                            ? "❌"
                            : "✅"}{" "}
                        {resp?.status ?? "N/A"} –{" "}
                        {resp?.statusText ?? "No status text"}
                      </Typography>

                      {/* Timestamp */}
                      <Typography variant="caption" color="text.secondary">
                        🕒{" "}
                        {resp?.checkedAt
                          ? new Date(resp.checkedAt).toLocaleString()
                          : "No timestamp available"}
                      </Typography>

                      {/* Response Headers */}
                      {resp?.headers &&
                        Object.keys(resp.headers).length > 0 && (
                          <Accordion sx={{ width: "100%", mt: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography>📥 Response Headers</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {Object.entries(resp.headers).map(([k, v]) => (
                                <Typography key={k} variant="body2">
                                  <strong>{k}:</strong> {String(v)}
                                </Typography>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )}

                      {/* Response Body */}
                      {resp?.data && (
                        <Accordion sx={{ width: "100%", mt: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>📦 Response Body</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <pre
                              style={{
                                whiteSpace: "pre-wrap",
                                maxHeight: 200,
                                overflow: "auto",
                                margin: 0,
                              }}
                            >
                              {JSON.stringify(resp.data, null, 2)}
                            </pre>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography>No response history available.</Typography>
            );
          })()}

        </DialogContent>


        <DialogActions>
          <Button onClick={() => setIsViewDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Card>
  );
}

export default DomainAddressDataTable;
